import React, { useEffect, useId, useState } from 'react';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getLanguage, t } from '../i18n';
import apiClient from '../services/apiClient';
import MarkdownText from './MarkdownText';
import vocabularyHomeImage from '../assets/home-icons/vocabulary-home-matte.webp';
import './HomeLearningPreviews.css';
import './VocabularyHomeEntry.css';

const koreaDay = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

export default function VocabularyHomeEntry({ userId }) {
  const titleId = useId();
  const [retry, setRetry] = useState(0);
  const [preview, setPreview] = useState({ owner: userId, loading: true, data: null, error: false });

  useEffect(() => {
    let active = true; let inFlight = false; let loadedDay = koreaDay();
    setPreview({ owner: userId, loading: true, data: null, error: false });
    const load = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        const data = await apiClient.getVocabularyHomePreview();
        if (!data || !Number.isSafeInteger(data.totalCount) || data.totalCount < 0) throw new Error('Invalid vocabulary preview');
        if (!active) return;
        loadedDay = data.day || koreaDay();
        setPreview({ owner: userId, loading: false, data, error: false });
      } catch {
        if (active) setPreview({ owner: userId, loading: false, data: null, error: true });
      } finally { inFlight = false; }
    };
    const refreshVisible = () => { if (!document.hidden) load(); };
    load();
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    const timer = window.setInterval(() => {
      if (koreaDay() !== loadedDay) refreshVisible();
    }, 60000);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [userId, retry]);

  const { data, loading, error } = preview.owner === userId ? preview : { loading: true };
  const word = typeof data?.entry?.text === 'string' && data.entry.text.trim() ? data.entry : null;
  const placeholder = loading ? t('common.loading') : data?.availability === 'all_complete' ? t('vocabulary.complete') :
    data?.availability === 'inactive' ? t('vocabulary.inactive') : data?.totalCount === 0 ?
      t('vocabulary.emptyBooksDescription') : t('vocabulary.homeNoWord');
  const count = data ? t('vocabulary.homeTotal', undefined, {
    count: new Intl.NumberFormat(getLanguage() === 'ko' ? 'ko-KR' : 'en-US').format(data.totalCount),
  }) : t('vocabulary.homeTotalUnknown');

  return <section className="vocabulary-home-entry" aria-labelledby={titleId}>
    <Link className="vocabulary-home-link home-learning-preview" to="/vocabulary">
      <header className="home-learning-preview__header">
        <img className="home-learning-preview__art" src={vocabularyHomeImage} alt="" aria-hidden="true" width={52} height={52} />
        <h2 id={titleId}>{t('vocabulary.homeTitle')}</h2>
      </header>
      <div className="home-learning-preview__viewport">
        {word ? <span className="home-learning-preview__stage">
          <span className="home-learning-preview__text" lang={word.learningLocale}
            title={t(data.kind === 'today' ? 'vocabulary.homeTodayWord' : 'vocabulary.homeUpcomingWord')}>
            <MarkdownText text={word.text} />
          </span>
        </span> : <span className="home-learning-preview__placeholder" role={loading ? 'status' : undefined}>{placeholder}</span>}
      </div>
      <span className="home-learning-preview__footer">
        <span className="home-learning-preview__count">{count}</span>
        <ArrowRight size={18} aria-hidden="true" />
      </span>
    </Link>
    {error && <div className="home-learning-previews__error" role="alert">
      <span>{t('vocabulary.homePreviewFailed')}</span>
      <button type="button" onClick={() => setRetry(value => value + 1)}>
        <RefreshCw size={16} aria-hidden="true" />{t('common.retry')}
      </button>
    </div>}
  </section>;
}
